import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, Tag, Button, Input, Form, Select, Row, Col, Empty, Segmented,
  Dropdown, Modal, Descriptions, Tooltip,
} from 'antd';
import {
  PlusOutlined, BankOutlined, TeamOutlined, MoreOutlined, EditOutlined,
  KeyOutlined, DeleteOutlined, StopOutlined, CheckCircleOutlined, EyeOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import KpiCards from '../../components/KpiCards';
import TableExportButton from '../../components/TableExportButton';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate, getUser } from '../../variables/constants';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import PhoneComponent from '../../components/PhoneComponent';
import CBModal from '../../components/CBModal';
import PageHeader from '../../components/PageHeader';
import { libellePaysOrganisme } from '../../variables/paysCedeao';
import { filtreSansAccents } from '../../utils/rechercheTexte';

const { Option } = Select;

export default function Utilisateurs() {
  const { t } = useTranslation('utilisateurs');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [profils, setProfils] = useState([]);
  const [compagnies, setCompagnies] = useState([]);
  const [bureaux, setBureaux] = useState([]);   // référentiel des bureaux homologues
  /* Les bureaux DÉJÀ confiés arrivent par un second appel, après l'ouverture de
     la modale. Tant qu'il n'a pas répondu, le champ ne reflète rien : enregistrer
     à cet instant enverrait une liste vide et EFFACERAIT en silence le
     paramétrage. Le champ reste donc désactivé jusqu'à la réponse, et la
     sauvegarde des bureaux est sautée si elle n'est jamais venue. */
  const [bureauxCharges, setBureauxCharges] = useState(false);
  const [destination, setDestination] = useState('BNCB');
  const [editing, setEditing] = useState(null);   // utilisateur en cours de modification
  const [busyId, setBusyId] = useState(null);     // action en cours sur une ligne
  const [detail, setDetail] = useState(null);     // utilisateur consulte en lecture seule

  // La session est alimentee par UserInfoResponse (dont la cle UUID s'appelle
  // `trackingId`, renommee en `utilisateurTrackingId` par authService) alors que la
  // liste vient de UtilisateurResponse : les deux DTO n'ont en commun que l'UUID et
  // l'email. C'est donc sur ces deux champs-la, et eux seuls, qu'on peut se
  // reconnaitre.
  const moi = getUser();

  // L'UUID prime ; l'email ne sert que si l'un des deux cotes n'en a pas. Sans ce
  // garde-fou sur la presence des UUID, une session ou la normalisation du login
  // n'a pas eu lieu ferait comparer `undefined === undefined` et le verrou
  // s'appliquerait a toutes les lignes sans identifiant - ou a aucune.
  const estMoiMeme = (u) => {
    if (!moi || !u) return false;
    if (moi.utilisateurTrackingId && u.utilisateurTrackingId) {
      return moi.utilisateurTrackingId === u.utilisateurTrackingId;
    }
    return !!moi.email && !!u.email
      && moi.email.toLowerCase() === u.email.toLowerCase();
  };

  // Le backend refuse qu'on change son propre profil d'acces. On desactive le
  // selecteur plutot que de laisser partir une requete vouee a un 403 : cet ecran
  // envoie TOUJOURS profilTrackingId, meme quand on n'edite que son telephone.
  // Le menu d'actions interdit desormais d'ouvrir l'edition de sa propre ligne,
  // mais on garde ce verrou : la modale reste atteignable si un autre chemin
  // appelle handleEdit, et sa disparition rouvrirait silencieusement le 403.
  const editionDeSoi = !!editing && estMoiMeme(editing);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, pRes] = await Promise.all([
        // Le backend renvoie la liste complète (page/size ignorés) - on pagine côté client.
        axiosInstance.get('/v1/utilisateurs').catch(() => ({ data: { data: [] } })),
        axiosInstance.get('/v1/profils').catch(() => ({ data: { data: [] } })),
      ]);
      setItems(uRes.data?.data || []);
      setProfils(pRes.data?.data || []);
    } finally { setLoading(false); }
  };

  const loadCompagnies = async () => {
    if (compagnies.length > 0) return;
    try {
      const res = await axiosInstance.get('/v1/organismes/by-type?typeOrganisme=COMPAGNIE_MEMBRE');
      const all = res.data?.data || [];
      setCompagnies(all.filter((c) => c.codePays === 'TGO' || c.codePaysBCB === 'TG'));
    } catch { /* silencieux */ }
  };

  /* Bureaux homologues - référentiel du multi-select « Bureaux suivis ».
     Chargé une fois pour toutes : la liste des bureaux CEDEAO tient en quinze
     lignes et ne bouge quasiment jamais. */
  const loadBureaux = async () => {
    if (bureaux.length > 0) return;
    try {
      const res = await axiosInstance.get('/v1/organismes/by-type?typeOrganisme=BUREAU_HOMOLOGUE');
      setBureaux(res.data?.data || []);
    } catch { /* silencieux : le champ restera vide, il n'est pas obligatoire */ }
  };

  useEffect(() => { load(); }, []);

  const handleOpenModal = () => {
    setEditing(null);
    setDestination('BNCB');
    form.resetFields();
    loadBureaux();
    setBureauxCharges(true);   // compte neuf : aucun paramétrage à attendre
    setModalOpen(true);
  };

  const handleEdit = (u) => {
    setEditing(u);
    setDestination(u.organismeRaisonSociale ? 'COMPAGNIE' : 'BNCB');
    if (u.organismeRaisonSociale) loadCompagnies();
    loadBureaux();
    form.setFieldsValue({
      nom: u.nom,
      prenoms: u.prenoms,
      email: u.email,
      telephone: u.telephone,
      profilTrackingId: u.profilTrackingId,
      organismeTrackingId: u.organismeTrackingId,
      bureauxTrackingIds: [],
    });
    setBureauxCharges(false);
    setModalOpen(true);
    /* Les bureaux ne sont pas dans UtilisateurResponse : ils vivent dans leur
       propre table et se chargent à part. Posés après l'ouverture pour ne pas
       retarder l'affichage de la modale. */
    axiosInstance.get(`/v1/utilisateurs/${u.utilisateurTrackingId}/bureaux`)
      .then((r) => {
        form.setFieldValue('bureauxTrackingIds',
          (r.data?.data || []).map((b) => b.organismeTrackingId));
        setBureauxCharges(true);
      })
      .catch(() => { /* on laisse `bureauxCharges` à faux : le champ restera
                        désactivé et la sauvegarde des bureaux sera sautée,
                        plutôt que d'effacer un paramétrage qu'on n'a pas lu. */ });
  };

  /* ───── Actions sur une ligne ───── */

  const reinitialiser = (u) => Modal.confirm({
    title: t('reinitialisation.titre'),
    icon: <KeyOutlined style={{ color: '#d97706' }} />,
    content: (
      <>
        <Trans
          t={t}
          i18nKey="reinitialisation.contenu"
          values={{ nom: u.nom, prenoms: u.prenoms, email: u.email }}
          components={{ strong: <strong /> }}
        />
      </>
    ),
    okText: t('reinitialisation.confirmer'),
    okButtonProps: { danger: true },
    cancelText: t('actions.annuler'),
    onOk: async () => {
      setBusyId(u.utilisateurTrackingId);
      try {
        await axiosInstance.post(`/v1/utilisateurs/${u.utilisateurTrackingId}/reinitialiser-acces`);
        toast.success(t('reinitialisation.lienEnvoye', { email: u.email }));
        load();
      } catch (_) { /* toast géré par axiosInstance */ }
      finally { setBusyId(null); }
    },
  });

  const basculerActivation = (u) => {
    const actif = !u.active;
    Modal.confirm({
      title: actif ? t('activation.titreActiver') : t('activation.titreDesactiver'),
      icon: actif
        ? <CheckCircleOutlined style={{ color: '#1a6b3c' }} />
        : <StopOutlined style={{ color: '#d97706' }} />,
      content: actif
        ? <Trans t={t} i18nKey="activation.contenuActiver" values={{ nom: u.nom, prenoms: u.prenoms }} components={{ strong: <strong /> }} />
        : <Trans t={t} i18nKey="activation.contenuDesactiver" values={{ nom: u.nom, prenoms: u.prenoms }} components={{ strong: <strong /> }} />,
      okText: actif ? t('actions.activer') : t('actions.desactiver'),
      cancelText: t('actions.annuler'),
      onOk: async () => {
        setBusyId(u.utilisateurTrackingId);
        try {
          await axiosInstance.patch(`/v1/utilisateurs/${u.utilisateurTrackingId}/activation?actif=${actif}`);
          toast.success(actif ? t('activation.compteActive') : t('activation.compteDesactive'));
          load();
        } catch (_) { /* toast géré par axiosInstance */ }
        finally { setBusyId(null); }
      },
    });
  };

  const supprimer = (u) => Modal.confirm({
    title: t('suppression.titre'),
    icon: <DeleteOutlined style={{ color: '#dc2626' }} />,
    content: (
      <>
        <Trans
          t={t}
          i18nKey="suppression.contenu"
          values={{ nom: u.nom, prenoms: u.prenoms, email: u.email }}
          components={{ strong: <strong /> }}
        />
      </>
    ),
    okText: t('actions.supprimer'),
    okButtonProps: { danger: true },
    cancelText: t('actions.annuler'),
    onOk: async () => {
      setBusyId(u.utilisateurTrackingId);
      try {
        await axiosInstance.delete(`/v1/utilisateurs/${u.utilisateurTrackingId}`);
        toast.success(t('suppression.succes'));
        load();
      } catch (_) { /* toast géré par axiosInstance */ }
      finally { setBusyId(null); }
    },
  });

  const handleDestinationChange = (val) => {
    setDestination(val);
    form.resetFields();
    if (val === 'COMPAGNIE') loadCompagnies();
  };

  const creer = async (vals) => {
    if (creating) return;
    setCreating(true);
    try {
      const payload = destination === 'COMPAGNIE'
        ? { nom: vals.nom, prenoms: vals.prenoms, email: vals.email, telephone: vals.telephone, organismeTrackingId: vals.organismeTrackingId }
        : { nom: vals.nom, prenoms: vals.prenoms, email: vals.email, telephone: vals.telephone, profilTrackingId: vals.profilTrackingId };

      let trackingId = editing?.utilisateurTrackingId;
      if (editing) {
        await axiosInstance.put(`/v1/utilisateurs/${editing.utilisateurTrackingId}`, payload);
        toast.success(t('enregistrement.modifie'));
      } else {
        const res = await axiosInstance.post('/v1/utilisateurs', payload);
        trackingId = res.data?.data?.utilisateurTrackingId;
        toast.success(t('enregistrement.cree'));
      }

      /* Les bureaux suivis vivent dans leur propre table : second appel, après
         que le compte existe. Un échec ici ne doit pas faire croire que la
         création a échoué - le compte, lui, est bien enregistré. Le parametrage
         des bureaux n'oriente que l'affichage des affectations en attente, il ne conditionne
         aucun accès : le rattraper plus tard ne coûte rien.
         Réservé aux agents internes : un gestionnaire de compagnie n'instruit
         pas de dossier et n'a donc aucun bureau à suivre. */
      if (trackingId && destination !== 'COMPAGNIE' && bureauxCharges) {
        try {
          await axiosInstance.put(`/v1/utilisateurs/${trackingId}/bureaux`,
            { organismeTrackingIds: vals.bureauxTrackingIds || [] },
            { skipErrorToast: true });
        } catch (e) {
          toast.warning(e?.response?.data?.message
            || t('enregistrement.bureauxNonSauvegardes'));
        }
      }
      form.resetFields();
      setEditing(null);
      setModalOpen(false);
      load();
    } catch (_) { /* toast géré par axiosInstance */ }
    finally { setCreating(false); }
  };

  const isCompagnie = destination === 'COMPAGNIE';

  // Profils internes BNCB. Depuis le correctif tenant du 2026-07-30, les profils
  // internes PORTENT l'organisme Bureau National (organisme_id=1) - TenantSecurity
  // refuse un utilisateur sans organisme. L'ancien filtre « interne = sans
  // organisme » ne matchait donc plus rien : liste de profils vide en prod,
  // creation d'utilisateur impossible. Le critere est desormais le TYPE de
  // l'organisme porteur ; l'absence d'organisme reste acceptee pour les bases
  // ou le correctif n'a pas encore ete rejoue.
  const profilsBncb = profils.filter(
    (p) => !p.organismeTrackingId || p.organismeTypeOrganisme === 'BUREAU_NATIONAL',
  );

  const tsConnexion = (u) => (u?.dateDeConnexion ? new Date(u.dateDeConnexion).getTime() : -Infinity);

  // Affichage de la dernière connexion : « Aujourd'hui à HH:mm » si c'est le jour même.
  const formatConnexion = (v) => {
    if (!v) return '-';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '-';
    const now = new Date();
    const memeJour = d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate();
    if (memeJour) {
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return t('connexion.aujourdhui', { hh, mm });
    }
    return formatDate(v);
  };

  // Les compteurs portent sur la liste COMPLETE et non sur `filtered` : ils decrivent
  // la population d'utilisateurs, pas le resultat de la recherche en cours. Les faire
  // suivre la recherche ne ferait que dupliquer le « n utilisateurs » deja affiche par
  // la pagination, et on perdrait le seul point de l'ecran qui donne la vue d'ensemble.
  const kpis = useMemo(() => {
    const actifs = items.filter((u) => u.active).length;
    // On prefere « Jamais connectes » au nombre de profils distincts : ce dernier est
    // un inventaire qui appartient a l'ecran Profils, alors qu'un compte cree dont le
    // mail d'activation n'a jamais ete suivi est une anomalie qui se corrige ICI,
    // precisement avec les actions de ce tableau (renvoi d'acces, suppression).
    const jamaisConnectes = items.filter((u) => !u.dateDeConnexion).length;
    return [
      { label: t('indicateurs.total'), value: items.length, color: '#374151', bg: '#f4f6f5' },
      { label: t('indicateurs.actifs'), value: actifs, color: '#1a6b3c', bg: '#e8f5ed' },
      { label: t('indicateurs.inactifs'), value: items.length - actifs, color: '#991b1b', bg: '#fee2e2' },
      { label: t('indicateurs.jamaisConnectes'), value: jamaisConnectes, color: '#92400e', bg: '#fef3c7' },
    ];
  }, [items, t]);

  const filtered = items
    .filter((i) =>
      !search || `${i.nom} ${i.prenoms} ${i.email} ${i.organismeRaisonSociale || ''}`.toLowerCase().includes(search.toLowerCase())
    )
    // Tri par défaut : dernière connexion décroissante (jamais connectés en dernier).
    .sort((a, b) => tsConnexion(b) - tsConnexion(a));

  const cols = [
    // Numero d'ordre dans la page affichee : repere de lecture pour designer une
    // ligne. Hors export, la numerotation n'a pas de sens dans un fichier qu'on retrie.
    {
      title: t('colonnes.numero'), key: 'ordre', width: 56, align: 'center', exportable: false,
      render: (_, __, index) => <span style={{ fontWeight: 700, color: '#94a3b8' }}>{index + 1}</span>,
    },
    {
      title: t('colonnes.nomPrenom'), key: 'np',
      render: (_, r) => <span style={{ fontWeight: 600 }}>{r.nom} {r.prenoms}</span>,
      export: (_, r) => `${r.nom || ''} ${r.prenoms || ''}`.trim(),
    },
    { title: t('colonnes.email'), dataIndex: 'email', key: 'email', ellipsis: true },
    // {
    //   title: 'Username', dataIndex: 'username', key: 'un',
    //   render: (v) => <span style={{ fontFamily: 'var(--font-main)', color: '#1a6b3c' }}>{v}</span>,
    //   export: (v) => v || '',
    // },
    {
      title: t('colonnes.profil'), dataIndex: 'profilNom', key: 'profil', width: 230,
      render: (v) => v ? (
        <Tag color={v === 'GESTIONNAIRE_COMPAGNIE' ? 'blue' : 'green'} style={{ borderRadius: 20 }}>{v}</Tag>
      ) : '-',
      export: (v) => v || '',
    },
    {
      title: t('colonnes.organisme'), dataIndex: 'organismeRaisonSociale', key: 'org', ellipsis: true,
      render: (v) => v ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <BankOutlined style={{ color: '#6b7280', fontSize: 12 }} />{v}
        </span>
      ) : <span style={{ color: '#d1d5db' }}>BNCB</span>,
    },
    {
      // L'API renvoie « active » (booleen Java isActive → JSON active), pas « isActive ».
      title: t('colonnes.actif'), dataIndex: 'active', key: 'act', width: 80, align: 'center',
      format: 'bool',
      render: (v) => v
        ? <Tag color="green" style={{ borderRadius: 20 }}>{t('valeurs.oui')}</Tag>
        : <Tag color="red" style={{ borderRadius: 20 }}>{t('valeurs.non')}</Tag>,
    },
    {
      title: t('colonnes.derniereConnexion'), dataIndex: 'dateDeConnexion', key: 'll', width: 150,
      format: 'datetime',
      render: (v) => <span style={{ fontSize: 12, color: '#6b7280' }}>{formatConnexion(v)}</span>,
    },
    {
      title: t('colonnes.actions'), key: 'actions', width: 90, align: 'center',
      exportable: false, // colonne d'actions : hors export Excel/PDF
      render: (_, r) => {
        // Personne n'agit sur son propre compte : se desactiver ou se supprimer
        // ferme la session en cours sans qu'aucun autre administrateur ne soit
        // prevenu, et se reinitialiser le mot de passe depuis cet ecran revoque
        // l'acces de celui-la meme qui vient de cliquer.
        const soi = estMoiMeme(r);

        // On DESACTIVE plutot que de masquer : une entree qui disparait se lit comme
        // un droit manquant ou un bug, et l'administrateur va la chercher ailleurs.
        // Grisee avec son motif au survol, la regle s'explique d'elle-meme. Le
        // <span> est indispensable : Tooltip a besoin d'un noeud DOM porteur pour
        // recevoir le survol, un texte nu n'en fournit aucun.
        const bloque = (texte) => (soi ? (
          <Tooltip
            title={t('actions.interditSurSoi')}
            placement="left"
          >
            <span>{texte}</span>
          </Tooltip>
        ) : texte);

        return (
          <Dropdown
            trigger={['click']}
            disabled={busyId === r.utilisateurTrackingId}
            menu={{
              items: [
                // Seule action laissee ouverte sur soi-meme : elle ne mute rien.
                { key: 'detail', icon: <EyeOutlined />, label: t('actions.voirDetail') },
                { key: 'edit', icon: <EditOutlined />, label: bloque(t('actions.modifier')), disabled: soi },
                {
                  key: 'reset',
                  icon: <KeyOutlined />,
                  label: bloque(t('actions.reinitialiserMotDePasse')),
                  disabled: soi,
                },
                {
                  key: 'toggle',
                  icon: r.active ? <StopOutlined /> : <CheckCircleOutlined />,
                  label: bloque(r.active ? t('actions.desactiver') : t('actions.activer')),
                  disabled: soi,
                },
                { type: 'divider' },
                {
                  key: 'delete',
                  icon: <DeleteOutlined />,
                  label: bloque(t('actions.supprimer')),
                  danger: true,
                  disabled: soi,
                },
              ],
              onClick: ({ key }) => {
                if (key === 'detail') { setDetail(r); return; }
                // Ceinture et bretelles : `disabled` n'est qu'un etat d'affichage du
                // menu. Si une refonte du composant le laissait passer, cette garde
                // empeche quand meme la requete mutante de partir.
                if (soi) return;
                if (key === 'edit') handleEdit(r);
                else if (key === 'reset') reinitialiser(r);
                else if (key === 'toggle') basculerActivation(r);
                else if (key === 'delete') supprimer(r);
              },
            }}
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              loading={busyId === r.utilisateurTrackingId}
              aria-label={t('colonnes.actions')}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Layout title={t('page.titre')}>
      {/* Le chargement ramene toute la liste d'un coup et la recherche est cliente :
          `filtered.length` est donc le compte exact de ce que le tableau montre. */}
      <PageHeader
        titre={t('page.titre')}
        icone={<TeamOutlined />}
        compte={filtered.length}
        unite={t('page.unite')}
        actions={(
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModal}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
            {t('page.ajouter')}
          </Button>
        )}
      />

      <KpiCards items={kpis} columns={4} />

      <div className="cb-card" style={{ marginBottom: 16 }}>
        <Input.Search placeholder={t('page.recherche')} value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 400 }} allowClear />
      </div>

      <div className="cb-card" style={{ padding: 0 }}>
        <Table className="cb-table" dataSource={filtered} columns={cols} rowKey="utilisateurTrackingId"
          loading={loading}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => t('tableau.total', { count: total }),
          }}
          locale={{ emptyText: <Empty description={t('tableau.vide')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />
      </div>

      <TableExportButton
        floating
        data={filtered} columns={cols} filename="utilisateurs"
        title={t('page.titre')} subtitle={search ? t('export.recherche', { search }) : t('export.tous')}
        count={filtered.length}
      />

      <CBModal
        title={editing ? t('modale.titreModifier') : t('modale.titreAjouter')}
        open={modalOpen}
        onCancel={() => { if (!creating) { setModalOpen(false); setEditing(null); form.resetFields(); } }}
        footer={null} width={540}
        maskClosable={!creating} closable={!creating}
      >
        {/* Toggle BNCB / Compagnie - le rattachement ne se change pas après coup */}
        <div style={{ marginBottom: 24 }}>
          <Segmented
            value={destination}
            onChange={handleDestinationChange}
            block
            disabled={!!editing}
            options={[
              { label: <span><TeamOutlined style={{ marginRight: 6 }} />{t('modale.personnelBncb')}</span>, value: 'BNCB' },
              { label: <span><BankOutlined style={{ marginRight: 6 }} />{t('modale.compagnieMembre')}</span>, value: 'COMPAGNIE' },
            ]}
          />
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, marginBottom: 0 }}>
            {editing
              ? t('modale.aideModification')
              : isCompagnie
                ? t('modale.aideCompagnie')
                : t('modale.aideBncb')}
          </p>
        </div>

        <Form form={form} layout="vertical" onFinish={creer}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="nom" label={t('champs.nom')} rules={[{ required: true }]}>
                <Input placeholder="KWATCHA" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="prenoms" label={t('champs.prenoms')} rules={[{ required: true }]}>
                <Input placeholder="Ama Abalo" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="email" label={t('modale.emailIdentifiant')} rules={[{ required: true, type: 'email' }]}>
                <Input placeholder={isCompagnie ? 'ama@compagnie.tg' : 'ama.kwatcha@bncb-togo.com'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="telephone" label={t('champs.telephone')}
                rules={[{ required: true, message: t('modale.requis') }, { len: 8, message: t('modale.huitChiffres') }]}>
                <PhoneComponent />
              </Form.Item>
            </Col>
            <Col span={12}>
              {isCompagnie ? (
                <Form.Item name="organismeTrackingId" label={t('modale.compagnie')} rules={[{ required: true, message: t('modale.requis') }]}>
                  <Select
                    showSearch
                    filterOption={filtreSansAccents}
                    placeholder={t('modale.selectionnerCompagnie')}
                    optionFilterProp="label"
                    options={compagnies.map((c) => ({
                      value: c.organismeTrackingId,
                      label: c.raisonSociale,
                    }))}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="profilTrackingId"
                  label={t('champs.profil')}
                  rules={[{ required: true }]}
                  extra={editionDeSoi
                    ? t('modale.profilPropreInterdit')
                    : undefined}
                >
                  <Select placeholder={t('modale.selectionnerProfil')} disabled={editionDeSoi}>
                    {profilsBncb.map((p) => (
                      <Option key={p.profilTrackingId} value={p.profilTrackingId}>{p.profilNom}</Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Col>
          </Row>

          {/* Réservé aux agents internes : un gestionnaire de compagnie n'instruit
              pas de dossier sinistre et n'a donc aucun bureau à suivre. */}
          {!isCompagnie && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="bureauxTrackingIds"
                  label={t('modale.bureauxSuivis')}
                  extra={t('modale.bureauxSuivisAide')}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    filterOption={filtreSansAccents}
                    loading={!bureauxCharges}
                    disabled={!bureauxCharges}
                    placeholder={bureauxCharges ? t('modale.aucunBureau') : t('modale.chargement')}
                    optionFilterProp="label"
                    maxTagCount="responsive"
                    options={bureaux
                      .map((b) => ({
                        value: b.organismeTrackingId,
                        label: [b.raisonSociale, libellePaysOrganisme(b)].filter(Boolean).join(' - '),
                      }))
                      .sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {isCompagnie && (
            <div style={{
              background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
              padding: '10px 14px', fontSize: 12.5, color: '#1d4ed8', marginBottom: 16,
            }}>
              <Trans t={t} i18nKey="modale.profilAutomatique" components={{ strong: <strong /> }} />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => { setModalOpen(false); setEditing(null); form.resetFields(); }} disabled={creating}>
              {t('actions.annuler')}
            </Button>
            <Button type="primary" htmlType="submit" loading={creating} disabled={creating}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
              {editing ? t('modale.enregistrer') : t('modale.creer')}
            </Button>
          </div>
        </Form>
      </CBModal>

      {/* Consultation en lecture seule. Il n'existe pas de route /utilisateurs/:id :
          cette modale est le seul endroit ou l'on peut lire les champs que le tableau
          ne montre pas (identifiant de connexion, telephone, 2FA, date de creation,
          derniere deconnexion) sans passer par la modale d'edition, qui elle expose
          des champs modifiables et donc un risque d'enregistrement involontaire. */}
      <CBModal
        title={t('detail.titre')}
        open={!!detail}
        onCancel={() => setDetail(null)}
        width={760}
        footer={(
          <Button onClick={() => setDetail(null)}>{t('detail.fermer')}</Button>
        )}
      >
        {/* Sans cette garde, les `detail.xxx` seraient evalues sur `null` a la
            fermeture, pendant l'animation de sortie de la modale. */}
        {detail && (
          <Descriptions
            column={2}
            size="small"
            bordered
            labelStyle={{ color: '#6b7280', width: 170 }}
          >
            <Descriptions.Item label={t('champs.nom')}>{detail.nom || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('champs.prenoms')}>{detail.prenoms || '-'}</Descriptions.Item>

            <Descriptions.Item label={t('champs.email')} span={2}>{detail.email || '-'}</Descriptions.Item>

            <Descriptions.Item label={t('detail.identifiantConnexion')}>
              {detail.username
                ? <span style={{ fontFamily: 'var(--font-main)', color: '#1a6b3c' }}>{detail.username}</span>
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('champs.telephone')}>{detail.telephone || '-'}</Descriptions.Item>

            <Descriptions.Item label={t('champs.profil')}>
              {detail.profilNom ? (
                <Tag
                  color={detail.profilNom === 'GESTIONNAIRE_COMPAGNIE' ? 'blue' : 'green'}
                  style={{ borderRadius: 20 }}
                >
                  {detail.profilNom}
                </Tag>
              ) : '-'}
            </Descriptions.Item>
            {/* Un utilisateur sans organisme est un agent interne : la colonne du
                tableau affiche « BNCB », on reste coherent ici. */}
            <Descriptions.Item label={t('champs.organisme')}>
              {detail.organismeRaisonSociale || 'BNCB'}
            </Descriptions.Item>

            {/* Les caracteristiques de l'organisme n'ont de sens que pour un compte
                rattache a une compagnie ; les afficher a vide pour le personnel BNCB
                ajouterait quatre lignes de tirets a chaque ouverture. */}
            {detail.organismeTrackingId && (
              <Descriptions.Item label={t('detail.codeOrganisme')}>{detail.organismeCode || '-'}</Descriptions.Item>
            )}
            {detail.organismeTrackingId && (
              <Descriptions.Item label={t('detail.paysOrganisme')}>{detail.organismeCodePays || '-'}</Descriptions.Item>
            )}
            {detail.organismeTrackingId && (
              <Descriptions.Item label={t('detail.emailOrganisme')}>{detail.organismeEmail || '-'}</Descriptions.Item>
            )}
            {detail.organismeTrackingId && (
              <Descriptions.Item label={t('detail.responsableOrganisme')}>{detail.organismeResponsable || '-'}</Descriptions.Item>
            )}

            <Descriptions.Item label={t('detail.compteActif')}>
              {detail.active
                ? <Tag color="green" style={{ borderRadius: 20 }}>{t('valeurs.oui')}</Tag>
                : <Tag color="red" style={{ borderRadius: 20 }}>{t('valeurs.non')}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={t('detail.doubleAuthentification')}>
              {detail.twoFactorEnabled
                ? <Tag color="green" style={{ borderRadius: 20 }}>{t('valeurs.activee')}</Tag>
                : <Tag style={{ borderRadius: 20 }}>{t('valeurs.desactivee')}</Tag>}
            </Descriptions.Item>

            <Descriptions.Item label={t('detail.motDePasseARenouveler')}>
              {detail.mustChangePassword
                ? <Tag color="orange" style={{ borderRadius: 20 }}>{t('valeurs.oui')}</Tag>
                : <Tag style={{ borderRadius: 20 }}>{t('valeurs.non')}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={t('detail.creeLe')}>
              {detail.createdAt ? formatDate(detail.createdAt) : '-'}
            </Descriptions.Item>

            <Descriptions.Item label={t('detail.derniereConnexion')}>
              {formatConnexion(detail.dateDeConnexion)}
            </Descriptions.Item>
            <Descriptions.Item label={t('detail.derniereDeconnexion')}>
              {detail.dateDeDeconnexion ? formatDate(detail.dateDeDeconnexion) : '-'}
            </Descriptions.Item>

            {/* Utile au support : c'est cet UUID qui apparait dans les URL de l'API
                et dans les logs backend quand on doit tracer une action. */}
            <Descriptions.Item label={t('detail.identifiantTechnique')} span={2}>
              <span style={{ fontSize: 12, color: '#6b7280', wordBreak: 'break-all' }}>
                {detail.utilisateurTrackingId || '-'}
              </span>
            </Descriptions.Item>
          </Descriptions>
        )}
      </CBModal>
    </Layout>
  );
}
